import type { ReactNode } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { Button } from '@scholastic/mockingjay';
import BackButtonIcon from '@data/assets/backButton.svg';
import { AdminFilterDrawer } from '@data/components/report/AdminReportDrawers/AdminReportDrawers';
import {
  DateRangeDropdown,
  type DateRange,
} from '@data/components/report/ReportFilterBar/dropdowns/DateRangeDropdown/DateRangeDropdown';

const DATE_FORMAT = 'MM/dd/yyyy';

export interface AdminFilterBarProps {
  /** The organization the signed-in admin is acting for — the card's title. */
  orgName?: string;
  dateRange: DateRange;
  /** Bumped on reset, to remount the dropdown back to its default option. */
  dateResetKey?: number;
  /** Seeds the dropdown as a custom range when the URL carries one. */
  initialCustomRange?: DateRange | null;
  onDateRangeChange: (range: DateRange, optionValue: string) => void;
  /**
   * The filters, narrowest scope last. Each owns its own data and decides
   * whether it applies at all, so a level that has no use for one passes
   * nothing and the row simply closes up.
   */
  organizationFilter?: ReactNode;
  teacherFilter?: ReactNode;
  classFilter?: ReactNode;
  studentFilter?: ReactNode;
  onReset: () => void;
}

/**
 * The filter card at the top of the admin section. Its filters scope every
 * report beneath it, so it sits above both the nav and the report.
 */
export function AdminFilterBar({
  orgName,
  dateRange,
  dateResetKey,
  initialCustomRange = null,
  onDateRangeChange,
  organizationFilter,
  teacherFilter,
  classFilter,
  studentFilter,
  onReset,
}: AdminFilterBarProps) {
  const dataRange = `Student data for ${format(
    dateRange.from,
    DATE_FORMAT
  )} \u2013 ${format(dateRange.to, DATE_FORMAT)}`;

  const dateFilter = (
    <div className="w-full tablet:w-[280px]">
      <DateRangeDropdown
        key={dateResetKey}
        onChange={onDateRangeChange}
        initialCustomRange={initialCustomRange}
      />
    </div>
  );

  const scopeFilters = (
    <>
      {organizationFilter}
      {teacherFilter}
      {classFilter}
      {studentFilter}

      <Button variant="tertiary" onClick={onReset} className="self-end">
        Reset filters
      </Button>
    </>
  );

  return (
    <div className="relative mb-6 rounded-lg bg-white p-6 pt-9 shadow-md">
      <div className="absolute left-0 top-0 h-3 w-full rounded-t-lg bg-gradient-to-r from-purple-700 to-red-600" />

      <div className="flex items-start gap-3">
        <Link
          to="/data/product-landing"
          aria-label="Back to product landing"
          className="shrink-0"
        >
          <BackButtonIcon aria-hidden className="h-8 w-8" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold antialiased [overflow-wrap:anywhere] tablet:text-3xl">
            {orgName}
          </h1>
          <p className="mt-2 text-sm text-neutral-1100 laptop:hidden">
            {dataRange}
          </p>
        </div>

        {/* Narrow enough that the filters have no room on the card: they move
            into a drawer this opens, and the card keeps its title and range. */}
        <AdminFilterDrawer>
          {dateFilter}
          {scopeFilters}
        </AdminFilterDrawer>
      </div>

      <div className="mt-4 hidden flex-col gap-4 mobile:items-stretch laptop:flex laptop:flex-row laptop:items-end">
        {dateFilter}
        {scopeFilters}
      </div>

      <p className="mt-3 hidden text-sm text-neutral-1100 laptop:block">
        {dataRange}
      </p>
    </div>
  );
}
